<template>
  <a-modal title="Violations List" width="600px" :visible="visible" @cancel="closeAuthorizationModal"
    :destroy-on-close="true" v-if="visible" data-testid="project-shift-auths-view-modal">
    <modal-navigation v-if="shiftsList.length && currentIndex !== -1" :current="currentIndex" :disabled="isLoading"
      :total="shiftsList.length" test-id="project-shift-auths-view-navigation"
      previous-button-test-id="project-shift-auths-view-previous-button"
      next-button-test-id="project-shift-auths-view-next-button" @navigate="navigate" />

    <div v-if="isLoading" class="loading-spin" data-testid="project-shift-auths-view-loading">
      <a-spin />
    </div>
    <div v-else class="flex flex-column gap-3" data-testid="project-shift-auths-view-content">
      <shift-auth-deny-form ref="shiftAuthDenyForm" :visible.sync="visibleDenyReason" :reason.sync="declineComment"
        :is-adjusted="true" :shift="shift"
        :authorizations="authorizations.filter(auth => selectedAuthorizations.includes(auth.id))"
        @confirmDeny="confirmDeny" />

      <a-modal
        width="600px"
        title="Accept Violation Comment"
        v-model="visibleAcceptComment"
        :destroy-on-close="true"
        data-testid="project-shift-auths-view-accept-modal"
      >
        <a-input
          placeholder="Optional"
          v-model="acceptComment"
          data-testid="project-shift-auths-view-accept-comment-input"
        />
        <template slot="footer">
          <div class="flex justify-content-between" data-testid="project-shift-auths-view-accept-footer">
            <a-button @click="cancelAccept" data-testid="project-shift-auths-view-accept-cancel-button">
              Cancel
            </a-button>
            <div class="flex justify-content-end">
              <a-button
                type="primary"
                @click="confirmAccept(false)"
                data-testid="project-shift-auths-view-accept-button"
              >
                Accept
              </a-button>
              <a-button type="primary" @click="confirmAccept(true)"
                data-testid="project-shift-auths-view-accept-adjust-button">
                Accept & Adjust
              </a-button>
            </div>
          </div>
        </template>
      </a-modal>
      <div v-if="shift" data-testid="project-shift-auths-view-shift-additional">
        <shift-additional :shift="shift" :associate="shift.internal_payment_info.associate" />
      </div>
      <shift-facility-administrator-contact
        v-if="showAdministratorContact"
        :associate-id="shift.internal_payment_info.associate.id"
      />
      <div
        class="flex gap-2 align-items-center"
        data-testid="project-shift-auths-view-select-all"
        v-if="
          shift &&
          hasUncontrolledPermission('manage_authorizations', {
            id: shift.internal_payment_info.associate.id
          })
        "
      >
        <a-checkbox
          @change="onSelectAllChange"
          :checked="selectedAuthorizations.length === authorizations.length"
          :indeterminate="indeterminate"
          data-testid="project-shift-auths-view-select-all-checkbox"
        />
        <h4 class="m-0">Select All</h4>
      </div>
      <a-tabs :default-active-key="defaultTabKey" data-testid="project-shift-auths-view-tabs">
        <a-tab-pane key="actual" v-if="actualAuthorizations.length"
          :tab="`Unreviewed (Total: ${actualAuthorizations.length})`">
          <div data-testid="project-shift-auths-view-actual-list">
            <shift-auth-list-modal :auths-list="actualAuthorizations" :associate="shift.internal_payment_info.associate"
              :version_created="shift.version_created_at" :selected-auths="selectedAuthorizations"
              :initial-page="getInitialPageIndex(actualAuthorizations)" :actions-blocked="isBlockedActions"
              @change="setAuthorizations" @delete="afterAuthDelete" />
          </div>
        </a-tab-pane>
        <a-tab-pane key="resolved" :tab="`Resolved (Total: ${resolvedAuthorizations.length})`">
          <div v-if="resolvedAuthorizations.length" data-testid="project-shift-auths-view-resolved-list">
            <shift-auth-list-modal :auths-list="resolvedAuthorizations" :selected-auths="selectedAuthorizations"
              :version_created="shift.version_created_at" :associate="shift.internal_payment_info.associate"
              :initial-page="getInitialPageIndex(resolvedAuthorizations)" :actions-blocked="isBlockedActions"
              @change="setAuthorizations" @delete="afterAuthDelete" />
          </div>
          <span v-else>No Resolved Violations Found</span>
        </a-tab-pane>
      </a-tabs>
    </div>
    <template slot="footer"
      ><div class="flex justify-content-between" data-testid="project-shift-auths-view-footer">
        <a-button @click="closeAuthorizationModal" data-testid="project-shift-auths-view-close-button">
          Cancel
        </a-button>
        <div
          class="flex gap-2"
          data-testid="project-shift-auths-view-actions"
          v-if="
            shift &&
            hasUncontrolledPermission('manage_authorizations', {
              id: shift.internal_payment_info.associate.id
            })
          "
        >
          <a-button
            type="danger"
            :loading="loadingDecline"
            :disabled="selectedAuthorizations.length == 0 || loadingAccept"
            @click="selectedAuthorizations.length > 1 ? denyAuthorizations() : (visibleDenyReason = true)"
            data-testid="project-shift-auths-view-deny-button">
            Deny
          </a-button>
          <a-button type="primary" :loading="loadingAccept"
            :disabled="selectedAuthorizations.length == 0 || loadingDecline" @click="visibleAcceptComment = true"
            data-testid="project-shift-auths-view-open-accept-modal-button">
            Accept
          </a-button>
        </div>
      </div>
    </template>
  </a-modal>
</template>

<script>
import ShiftAuthDenyForm from '@/modules/authorizations/components/shift-auth-deny.vue'
import ShiftAuthListModal from '@/modules/authorizations/components/table/shift-auth-list.vue'
import { createNamespacedHelpers } from 'vuex'
import { Button, Checkbox, Input, notification, Spin, Tabs } from 'ant-design-vue'
import AuthReasonMixin from '../../mixins/AuthReasonMixin'
import ShiftFormAdditional from '@/modules/shifts/components/shift-form-additional.vue'
import permissionMixin from '@/mixins/permissionMixin'
import ModalNavigation from '@/components/entities/modal/modal-navigation.vue'
import ShiftFacilityAdministratorContact from '@/modules/authorizations/components/shift-facility-administrator-contact.vue'

const { mapActions } = createNamespacedHelpers('shifts')

export default {
  components: {
    'a-button': Button,
    'a-spin': Spin,
    'a-input': Input,
    'a-tabs': Tabs,
    'a-tab-pane': Tabs.TabPane,
    'a-checkbox': Checkbox,
    'modal-navigation': ModalNavigation,
    'shift-auth-list-modal': ShiftAuthListModal,
    'shift-auth-deny-form': ShiftAuthDenyForm,
    'shift-additional': ShiftFormAdditional,
    'shift-facility-administrator-contact': ShiftFacilityAdministratorContact
  },
  props: {
    shiftId: {
      type: Number
    },
    initialAuth: {
      type: String,
      default: undefined
    },
    visible: {
      type: Boolean,
      default: false
    },
    shiftsList: {
      type: Array,
      default: () => []
    },
    getFiltered: {
      type: Function,
      default: () => { }
    }
  },
  mixins: [AuthReasonMixin, permissionMixin],
  data() {
    return {
      visibleDenyReason: false,
      visibleAcceptComment: false,

      loadingDecline: false,
      loadingAccept: false,

      selectedAuthorizations: [],

      isLoading: false,

      acceptComment: undefined,
      declineComment: undefined,

      shift: undefined,
      authorizations: [],

      currentIndex: -1
    }
  },
  computed: {
    defaultTabKey() {
      if (this.initialAuth) {
        const inActual = this.actualAuthorizations.some(item => item.type === this.initialAuth)
        const inResolved = this.resolvedAuthorizations.some(item => item.type === this.initialAuth)

        if (inActual) return 'actual'
        if (inResolved) return 'resolved'
      }

      return this.actualAuthorizations.length ? 'actual' : 'resolved'
    },
    showAdministratorContact() {
      return (
        !!this.shift?.internal_payment_info?.associate?.id &&
        (this.isBlockedActions ||
          !this.hasUncontrolledPermission('manage_authorizations', {
            id: this.shift.internal_payment_info.associate.id
          }))
      )
    },
    indeterminate() {
      return !!this.selectedAuthorizations.length && this.selectedAuthorizations.length < this.authorizations.length
    },
    fullSelectedAuths() {
      if (this.authorizations.length === 0 || this.authorizations.length === 0) {
        return []
      }

      return this.authorizations.filter(auth => this.selectedAuthorizations.includes(auth.id))
    },
    actualAuthorizations() {
      return this.authorizations.filter(item => !item.deleted_at && item.status === 'initial')
    },
    resolvedAuthorizations() {
      return this.authorizations.filter(item => item.deleted_at || item.status !== 'initial')
    },
    isBlockedActions() {
      return (
        this.$store.state.applicationState.currentProject.staffing &&
        this.shift?.external_payment_info &&
        this.shift?.external_payment_info?.project?.id !== this.$store.state.applicationState.currentProject.id
      )
    }
  },
  watch: {
    visible: {
      handler(newVal) {
        this.currentIndex = this.shiftsList.findIndex(item => item === this.shiftId)

        if (newVal) this.fetchAuthorizations()
      },
      immediate: true
    }
  },
  methods: {
    ...mapActions(['acceptShiftAuthorization', 'declineShiftAuthorization', 'getShiftWithAuth']),

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },

    getInitialPageIndex(list) {
      if (!this.initialAuth) return 0
      const index = list.findIndex(item => item.type === this.initialAuth)
      return index >= 0 ? index : 0
    },

    onSelectAllChange() {
      if (this.selectedAuthorizations.length === this.authorizations.length) {
        this.selectedAuthorizations = []
      } else {
        this.selectedAuthorizations = this.authorizations.map(item => item.id)
      }
    },

    afterAuthDelete(id) {
      this.authorizations = this.authorizations.filter(item => item.id !== id)

      this.$emit('update')
    },

    async navigate(index) {
      this.currentIndex = index
      this.authorizations = []
      this.selectedAuthorizations = []
      this.$emit('update:shiftId', this.shiftsList[index])
      this.$emit('update:initialAuth', undefined)

      this.$nextTick(async () => {
        await this.fetchAuthorizations()
      })
    },

    closeAuthorizationModal() {
      this.authorizations = []
      this.selectedAuthorizations = []
      this.$emit('update:visible', false)
      this.$emit('update:shiftId', undefined)
      this.$emit('update:initialAuth', undefined)
    },

    // setters

    setAuthorizations(authorizations) {
      this.selectedAuthorizations = authorizations
    },

    confirmDeny() {
      if (this.declineComment && this.selectedAuthorizations && this.selectedAuthorizations.length > 0) {
        this.denyAuthorizations()
      } else {
        notification.warning({
          message: 'Error',
          description: 'Please fill deny reason and try again'
        })
      }
    },

    cancelAccept() {
      this.visibleAcceptComment = false
      this.acceptComment = ''
    },

    confirmAccept() {
      this.visibleAcceptComment = false

      if (this.selectedAuthorizations && this.selectedAuthorizations.length > 0) {
        this.acceptAuthorizations()
      }
    },

    async fetchAuthorizations() {
      this.isLoading = true
      try {
        const { shift, authorizations } = await this.getShiftWithAuth({
          shift_id: this.shiftId
        })

        this.shift = shift
        this.authorizations = this.getFiltered(authorizations)
      } catch (error) {
        this.showNotification('error', 'Error', error.message || error)
      } finally {
        this.isLoading = false
      }
    },

    async acceptAuthorizations() {
      this.loadingAccept = true
      try {
        const payload = {
          auth_ids: this.selectedAuthorizations,
          comment: this.acceptComment,
          review_context: this.fullSelectedAuths.map(authorization => ({
            authorization,
            associate: this.shift.internal_payment_info.associate
          }))
        }

        const { authorizations, cancelling } = await this.acceptShiftAuthorization(payload)
        if (cancelling) this.loadingAccept = false
        else {
          this.showNotification('success', 'Success', 'Violations accepted successfully')

          this.$emit('update', {
            authorizations,
            shift_id: this.shiftId
          })
          this.closeAuthorizationModal()
        }
      } catch (error) {
        this.showNotification('error', 'Error', error.message || error)
      } finally {
        this.loadingAccept = false
      }
    },

    async denyAuthorizations() {
      this.loadingDecline = true
      try {
        const payload = {
          auth_ids: this.selectedAuthorizations,
          comment:
            this.selectedAuthorizations.length > 1
              ? this.fullSelectedAuths.map(item => {
                return this.getFullReason(item.type, this.shift)
              })
              : this.selectedAuthorizations.map(() => this.declineComment)
        }

        const authorizations = await this.declineShiftAuthorization(payload)

        this.showNotification('success', 'Success', 'Violations declined successfully')
        this.$emit('update', {
          authorizations: authorizations,
          shift_id: this.shiftId
        })
        this.closeAuthorizationModal()
      } catch (error) {
        this.showNotification('error', 'Error', error.message || error)
      } finally {
        this.loadingDecline = false
      }
    }
  }
}
</script>
